import React from 'react';

import { NavigateFunction } from 'react-router-dom';

import {
  Agencies,
  IHistoryData,
  INotes,
  IPharmacyNotes,
  IPharmacyReplacementData,
  IReplacementsData,
  ITEMS_PER_PAGE,
  ProfessionalReplacementTypes,
  ServiceTypes,
  TimeFormat,
} from '@pharmaplan/common';
import updateIcon from '@pharmaplan/common/assets/icons/updateNote.svg';

import star from '@pharmaplan/common/assets/icons/starNotFilled.svg';
import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import { Box } from '@mui/system';
import { ReactComponent as SmallStar } from '../../assets/svg/smallStar.svg';
import {
  Constants,
  DynamicTableCell,
  ReplacementLabels,
} from '../../helpers/Constants';
import { getDayMonthDateYear, getHourMinute } from '../Reports/helpers';
import historyIcon from '../../assets/svg/historyIcon.svg';
import notesIcon from '../../assets/svg/notesIcon.svg';
import deleteIcon from '../../assets/svg/delete.svg';
import generateInvoice from '../../assets/svg/generateInvoice.svg';
import strings from '../../localization';

import genericClasses from '../../theme/GenericClasses';
import style from './style';

const isSelfService = ServiceHandler.getService() === ServiceTypes.self;
const keys = {
  fav: 'fav',
  rating: 'rating',
  replacementType: 'replacementType',
};

const keysToRemove = [keys.fav, keys.rating];

export const pharmacistHeaders = (isAutoSyncRecord?: boolean) => {
  const isSyncedRecord = isAutoSyncRecord && isSelfService;
  let arr = [
    { key: '0', label: '#' },
    { key: '1', label: strings.banner },
    { key: '2', label: strings.pharmacy, sortingKey: 'pharmacyName' },
    isSelfService && {
      key: keys.rating,
      label: strings.rating,
      isCenterAligned: true,
      sortingKey: 'rating',
    },
    { key: keys.fav, label: strings.favourite, sortingKey: 'favourite' },
    { key: '4', label: strings.action },
  ].filter(Boolean);

  if (isSyncedRecord) {
    arr = arr.filter((item) =>
      !!item && !keysToRemove.includes(item?.key));
  }

  return arr;
};

export const headers = (isExternalPharmacist?: boolean) => {
  if (isSelfService) {
    return [
      { key: '0', label: '#' },
      { key: '1', label: strings.name, sortingKey: 'name' },
      {
        key: '2',
        label: strings.rating,
        isCenterAligned: true,
        sortingKey: 'rating',
      },
      {
        key: '3',
        label: strings.totalReplacements,
        isCenterAligned: true,
        sortingKey: 'totalReplacements',
      },
      {
        key: '4',
        label: strings.replacementType,
        sortingKey: 'replacementType',
      },
      {
        key: '5',
        label: strings.totalHours,
        isCenterAligned: true,
        sortingKey: 'totalHours',
      },
      { key: '6', label: strings.favourite, sortingKey: 'favourite' },
      { key: '7', label: strings.compatibility },
      { key: '8', label: strings.action, isCenterAligned: true },
    ];
  }

  let arr = [
    { key: '0', label: '#' },
    { key: '1', label: strings.name, sortingKey: 'name' },
    {
      key: '2',
      label: strings.rating,
      isCenterAligned: true,
      sortingKey: 'rating',
    },
    {
      key: '3',
      label: strings.totalReplacements,
      isCenterAligned: true,
      sortingKey: 'totalReplacements',
    },
    {
      key: '4',
      label: strings.totalHours,
      isCenterAligned: true,
      sortingKey: 'totalHours',
    },
    { key: keys.fav, label: strings.favourite, sortingKey: 'favourite' },
    { key: '6', label: strings.action, isCenterAligned: true },
  ];

  if (isExternalPharmacist) {
    arr = arr.filter((item) =>
      item.key !== keys.fav);
  }

  return arr;
};

export const pharmacistReplacementMapper = (
  data: Array<IReplacementsData>,
  navigate: NavigateFunction,
  currentIndex: number,
  getImage: (logoId: string) => string,
  goToRating: (pharmacyId: string) => () => void,
  openBookingReport: (pharmacyId: string, isAutoSyncRecord?: boolean) => void,
  source?: Agencies,
) =>
  data?.map((item, i) => {
    const { pharmacyId, pharmacyName, isAutoSyncRecord, notesExist } = item ?? {};
    const isNotSyncedRecord = isSelfService && !isAutoSyncRecord;

    // eslint-disable-next-line eqeqeq
    const isRatingNotAvailable = item?.rating == '0';

    const rating = (
      <Box sx={genericClasses.center}>
        {item.rating}
        <Box sx={style.star}>
          <SmallStar />
        </Box>
      </Box>
    );

    const value = [
      {
        key: '1',
        icon: historyIcon,
        tooltip: strings.history,
        showAlert: false,
        onClick: () =>
          navigate(Constants.paths.history, {
            state: {
              id: item.pharmacyId,
              name: item.pharmacyName,
              source,
            },
          }),
      },
    ];

    if (!isAutoSyncRecord) {
      value.push({
        key: '2',
        icon: notesIcon,
        tooltip: strings.notes,
        showAlert: !!notesExist,
        onClick: () =>
          navigate(Constants.paths.notes, {
            state: { id: pharmacyId, name: pharmacyName },
          }),
      });
    }

    if (isSelfService) {
      value.push({
        key: '4',
        showAlert: false,
        icon: generateInvoice,
        tooltip: strings.generateInvoice,
        onClick: () =>
          openBookingReport(pharmacyId, isAutoSyncRecord),
      });
    }

    if (isNotSyncedRecord) {
      value.push({
        key: '3',
        icon: star,
        showAlert: false,
        tooltip: strings.rate,
        onClick: () =>
          navigate(Constants.paths.history, {
            state: { id: pharmacyId, name: pharmacyName },
          }),
      });
    }

    let arr = [
      {
        key: `${item.pharmacyId}-1`,
        value: currentIndex + i + 1,
        type: DynamicTableCell.text,
      },
      {
        key: `${item.pharmacyId}-2`,
        value: getImage(item.logoId),
        type: DynamicTableCell.icon,
      },
      {
        key: `${item.pharmacyId}-3`,
        value: `${item.pharmacyName}\n${item.city}`,
        type: DynamicTableCell.text,
      },
      {
        key: keys.rating,
        value: isRatingNotAvailable
          ? rating
          : { label: rating, handlePress: goToRating(pharmacyId) },
        type: isRatingNotAvailable
          ? DynamicTableCell.text
          : DynamicTableCell.customLink,
        isCenterAligned: true,
      },
      {
        key: keys.fav,
        value: {
          checked: item.favourite,
        },
        type: DynamicTableCell.favourite,
      },
      {
        key: `${item.pharmacyId}-5`,
        value,
        type: DynamicTableCell.bar,
      },
    ];

    if (!isNotSyncedRecord) {
      arr = arr.filter((arrItem) =>
        arrItem.key !== keys.rating);
    }

    if (isAutoSyncRecord) {
      arr = arr.filter((arrItem) =>
        arrItem.key !== keys.fav);
    }

    return {
      key: item.pharmacyId,
      data: arr,
    };
  });

export const pharmacyReplacementMapper = (
  data: Array<IPharmacyReplacementData>,
  navigate: NavigateFunction,
  currentIndex: number,
  goToPharmacistDetails: (
    pharmacistId: string,
    isAutoSyncRecord?: boolean
  ) => () => void,
  goToRating: (pharmacistId: string) => () => void,
  source?: Agencies,
) =>
  data?.map((item, i) => {
    // eslint-disable-next-line eqeqeq
    const isRatingNotAvailable = item.rating == '0';
    const { pharmacistId, professionalId, externalPharmacistId } = item ?? {};
    const key = (isSelfService ? professionalId : pharmacistId) ?? '';
    const isReplacementTypePresent = item?.replacementType
      // eslint-disable-next-line eqeqeq
      || item?.replacementType == ProfessionalReplacementTypes.pharmacist;

    const rating = (
      <Box sx={genericClasses.center}>
        {item.rating}
        <Box sx={style.star}>
          <SmallStar />
        </Box>
      </Box>
    );

    const icons = [
      {
        key: '1',
        icon: historyIcon,
        tooltip: strings.history,
        onClick: () =>
          navigate(Constants.paths.history, {
            state: { id: key, name: item.name, source },
          }),
      },
    ];

    if (!externalPharmacistId) {
      icons.push(
        ...[
          {
            key: '2',
            icon: notesIcon,
            tooltip: strings.notes,
            onClick: () =>
              navigate(Constants.paths.notes, {
                state: { id: key, name: item.name },
              }),
          },
          {
            key: '3',
            icon: star,
            tooltip: strings.rate,
            onClick: () =>
              navigate(Constants.paths.history, {
                state: { id: key, name: item.name, source },
              }),
          },
        ],
      );
    }

    let arr = [
      {
        key: `${item.pharmacistId}-1`,
        value: currentIndex + i + 1,
        type: DynamicTableCell.text,
      },
      {
        key: `${item.pharmacistId}-2`,
        value: {
          label: item.name,
          handlePress: goToPharmacistDetails(key, !!externalPharmacistId),
        },
        type: DynamicTableCell.customLink,
      },
      {
        key: `${item.pharmacistId}-3`,
        value: isRatingNotAvailable
          ? rating
          : { label: rating, handlePress: goToRating(key) },
        type: isRatingNotAvailable
          ? DynamicTableCell.text
          : DynamicTableCell.customLink,
        isCenterAligned: true,
      },
      {
        key: `${item.pharmacistId}-3.5`,
        value: item.totalReplacements,
        type: DynamicTableCell.text,
        isCenterAligned: true,
      },
      {
        key: keys.replacementType,
        value:
          ReplacementLabels[
            item.replacementType ?? ProfessionalReplacementTypes.pharmacist
          ],
        type: DynamicTableCell.text,
      },
      {
        key: `${item.pharmacistId}-5`,
        value: Math.trunc(item.totalHours),
        type: DynamicTableCell.text,
        isCenterAligned: true,
      },
      {
        key: keys.fav,
        value: {
          checked: item.favourite,
          disabled: item.isIncompatible,
        },
        type: DynamicTableCell.favourite,
      },
      {
        show: isSelfService,
        key: `${item.pharmacistId}-6.5`,
        value: item.isIncompatible,
        type: DynamicTableCell.incompatible,
      },
      {
        key: `${item.pharmacistId}-7`,
        value: icons,
        isCenterAligned: true,
        type: DynamicTableCell.bar,
      },
    ];

    if (!isReplacementTypePresent) {
      arr = arr.filter(
        (dataItem) =>
          ![keys.replacementType].includes(dataItem.key),
      );
    }

    if (externalPharmacistId) {
      arr = arr.filter((dataItem) =>
        dataItem.key !== keys.fav);
    }

    return {
      key,
      data: arr,
    };
  });

export const pharmacistNotesMapper = (
  data: Array<INotes>,
  OpenModal: (id: string, update: boolean) => void,
  deleteAction: (id: string) => void,
) => {
  const { pharmacistNotesId } = idStore;
  return data?.map((item) => {
    const key = item[pharmacistNotesId as keyof typeof item] ?? '';
    return {
      key,
      data: [
        {
          key: `${item.pharmacyId}-1`,
          value: item.note,
          type: DynamicTableCell.notes,
        },
        {
          key: `${item.pharmacyId}-2`,
          value: getDayMonthDateYear(item.createDate),
          type: DynamicTableCell.date,
        },
        {
          key: `${item.pharmacyId}-3`,
          value: item.updateDate ? getDayMonthDateYear(item.updateDate) : '',
          type: DynamicTableCell.date,
        },
        {
          key: `${item.pharmacyId}-5`,
          value: [
            {
              key: '1',
              icon: updateIcon,
              onClick: () =>
                OpenModal(key, true),
            },
            {
              key: '2',
              icon: deleteIcon,
              onClick: () =>
                deleteAction(key),
            },
          ],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });
};

export const pharmacyNotesMapper = (
  data: Array<IPharmacyNotes>,
  OpenModal: (id: string, update: boolean) => void,
  deleteAction: (id: string) => void,
) =>
  data?.map((item) => {
    const notesId = item[
      idStore.pharmacyNotesId as keyof typeof item
    ] as string;
    return {
      key: item.pharmacyPharmacistNoteId,
      data: [
        {
          key: `${item.pharmacistId}-1`,
          value: item.note,
          type: DynamicTableCell.notes,
        },
        {
          key: `${item.pharmacistId}-2`,
          value: getDayMonthDateYear(item.createDate),
          type: DynamicTableCell.date,
        },
        {
          key: `${item.pharmacistId}-3`,
          value: item.updateDate ? getDayMonthDateYear(item.updateDate) : '',
          type: DynamicTableCell.date,
        },
        {
          key: `${item.pharmacistId}-5`,
          value: [
            {
              key: '1',
              icon: updateIcon,
              onClick: () =>
                OpenModal(notesId, true),
            },
            {
              key: '2',
              icon: deleteIcon,
              onClick: () =>
                deleteAction(notesId),
            },
          ],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });

export const pharmacyHistoryMapper = (
  data: Array<IHistoryData>,
  timeFormat: TimeFormat,
  OpenModal: (bookingId: string, canRate: boolean) => void,
  page: number,
) =>
  data?.map((item, i) => {
    const {
      code,
      startDate,
      endDate,
      bookingId,
      canRatePharmacist,
      canRateProfessional,
    } = item ?? {};
    const canRate = isSelfService ? canRateProfessional : canRatePharmacist;

    return {
      key: item.code,
      data: [
        {
          key: `${code}-0`,
          value: 1 + i + (page - 1) * ITEMS_PER_PAGE,
          type: DynamicTableCell.text,
        },
        {
          key: `${code}-1`,
          value: code,
          type: DynamicTableCell.text,
        },
        {
          key: `${code}-3`,
          value: getDayMonthDateYear(startDate),
          type: DynamicTableCell.text,
        },
        {
          key: `${code}-2`,
          value: `${getHourMinute(startDate, timeFormat)} - ${getHourMinute(
            endDate,
            timeFormat,
          )}`,
          type: DynamicTableCell.text,
        },
        {
          key: `${code}-4`,
          value: [
            {
              key: '1',
              icon: star,
              disabled: !canRate,
              onClick: () =>
                OpenModal(bookingId, !!canRate),
            },
          ],
          isCenterAligned: true,
          type: DynamicTableCell.bar,
        },
      ],
    };
  });

export const pharmacistHistoryMapper = (
  data: Array<IHistoryData>,
  timeFormat: TimeFormat,
  OpenModal: (bookingId: string, canRate: boolean) => void,
  page: number,
) =>
  data?.map((item, i) =>
    ({
      key: item.code,
      data: [
        {
          key: `${item.code}-1`,
          value: 1 + i + (page - 1) * ITEMS_PER_PAGE,
          type: DynamicTableCell.text,
        },
        {
          key: `${item.code}-2.5`,
          value: item.code,
          type: DynamicTableCell.text,
        },
        {
          key: `${item.code}-3`,
          value: getDayMonthDateYear(item.startDate),
          type: DynamicTableCell.text,
        },
        {
          key: `${item.code}-2`,
          value: `${getHourMinute(item.startDate, timeFormat)} - ${getHourMinute(
            item.endDate,
            timeFormat,
          )}`,
          type: DynamicTableCell.text,
        },
        isSelfService && !item.isAutoSyncRecord && {
          key: `${item.code}-4`,
          value: [
            {
              key: '1',
              icon: star,
              onClick: () =>
                OpenModal(item.bookingId, true),
            },
          ],
          isCenterAligned: true,
          type: DynamicTableCell.bar,
        },
      ],
    }));
